import { type Dayjs } from "dayjs"

import {
  impliedGapDaysFor,
  sourceSideFor,
  targetSideFor
} from "../constants/milestone-dependency-relationship.ts"
import type {
  MilestoneDependency,
  MilestoneDependencyType
} from "../../types/project-milestone-dependency.types.ts"
import type { ProjectMilestone } from "../../types/project-phases.types.tsx"
import { isMilestoneFinished } from "./milestone-schedule-state.ts"

interface DateWindow {
  start: Dayjs
  end: Dayjs
}

interface ShiftReason {
  sourceMilestoneId: string
  type: MilestoneDependencyType
  lagDays: number
}

interface EffectiveScheduleEntry extends DateWindow {
  /** Whether a dependency pushed this milestone later than its own stored dates. */
  isShifted: boolean
  shiftedByDays: number
  /** The milestone has no Planned Start/End of its own — none stored, or the
   * ones stored were written by the server from its dependencies; this window
   * exists only because a dependency produced it. Never true together with
   * `isShifted`. */
  isGenerated: boolean
  /** The dependency edge that determined the effective start (a shift or a
   * generation), null when no dependency drove the result. */
  shiftReason: ShiftReason | null
  /** When actually finished — a separate fact from `end` (the planned/
   * effective end), never a substitute for it. Null while still in progress. */
  actualEnd: Dayjs | null
}

const anchorOf = (window: DateWindow, side: "start" | "end") =>
  side === "start" ? window.start : window.end

// Inclusive days, like `duration_days`: the fallback span for a generated
// window when the target has neither stored dates nor a duration of its own.
const DEFAULT_MILESTONE_DURATION_DAYS = 7

/**
 * The single source of truth for what a milestone's dates actually are once
 * its dependencies are taken into account. Both the Gantt and the Table read
 * from this rather than a milestone's raw Planned Start/End directly — a
 * milestone whose stored dates would otherwise violate a dependency (or did,
 * before an upstream milestone's dates changed) is reported at the earliest
 * date that satisfies every dependency it has, never at a date that visibly
 * precedes something it depends on.
 *
 * `overrides` lets a caller preview "what would the schedule look like if
 * milestone X had these dates / this dependency existed" without touching
 * real data — used to compute the impact of a pending edit before it's saved.
 */
interface EffectiveScheduleOverride extends Partial<DateWindow> {
  /** Explicit `null` clears a milestone's actual end for the preview;
   * `undefined` (the default, i.e. the key simply absent) leaves its real
   * stored value alone. */
  actualEnd?: Dayjs | null
}

/** Dated by the server from its dependencies (it has an Effort and the
 * read-only flag), unless the pending edit gives it dates of its own. */
const isDependencyDriven = (
  milestone: ProjectMilestone,
  override?: EffectiveScheduleOverride
) =>
  !!milestone.dates_from_dependencies &&
  (milestone.duration_days ?? 0) > 0 &&
  !override?.start &&
  !override?.end

function calculateEffectiveSchedule({
  milestones,
  dependencies,
  parseDate,
  overrides
}: {
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  parseDate: (value: unknown) => Dayjs | null
  overrides?: Map<string, EffectiveScheduleOverride>
}): Map<string, EffectiveScheduleEntry> {
  const milestoneById = new Map(milestones.map((milestone) => [milestone.id, milestone]))
  const incomingByTarget = new Map<string, MilestoneDependency[]>()
  const outgoingBySource = new Map<string, string[]>()
  const indegree = new Map<string, number>()

  dependencies.forEach((dep) => {
    // Both ends must be real milestones in this dataset — an edge touching
    // one that isn't (or isn't loaded) would corrupt the topological order
    // below. An undated target still gets a slot so a dependent further down
    // the chain can read its generated window once it has been resolved.
    if (!milestoneById.has(dep.source_milestone) || !milestoneById.has(dep.target_milestone)) {
      return
    }
    incomingByTarget.set(dep.target_milestone, [
      ...(incomingByTarget.get(dep.target_milestone) || []),
      dep
    ])
    outgoingBySource.set(dep.source_milestone, [
      ...(outgoingBySource.get(dep.source_milestone) || []),
      dep.target_milestone
    ])
    indegree.set(dep.target_milestone, (indegree.get(dep.target_milestone) || 0) + 1)
  })

  const ownWindowById = new Map<string, DateWindow>()
  const partiallyDatedIds = new Set<string>()
  // Once a milestone has actually finished, that's the real anchor a
  // Finish-based dependent must schedule off of — its Planned End keeps
  // representing the original plan and is never overwritten by this.
  const actualEndById = new Map<string, Dayjs>()
  // A finished milestone happened when it happened: its own dates stand and
  // no predecessor moves it. Its successors still plan from it.
  const finishedIds = new Set<string>()

  milestones.forEach((milestone) => {
    const override = overrides?.get(milestone.id)
    const actualEnd =
      override && "actualEnd" in override
        ? override.actualEnd
        : parseDate(milestone.actual_end_date)
    if (actualEnd) actualEndById.set(milestone.id, actualEnd)
    const finished = isMilestoneFinished({
      status: milestone.status,
      progress: milestone.progress,
      actual_end_date: actualEnd ? actualEnd.format("YYYY-MM-DD") : null
    })

    // Dates the server wrote from the dependencies are not anyone's plan: the
    // milestone is placed from its constraints alone, earlier as well as
    // later, exactly as the server will save it. Without a loaded predecessor
    // its saved dates stand.
    if (
      !finished &&
      isDependencyDriven(milestone, override) &&
      incomingByTarget.has(milestone.id)
    ) {
      return
    }

    const start = override?.start ?? parseDate(milestone.start_date)
    const end = override?.end ?? parseDate(milestone.end_date)
    if (start && end) {
      ownWindowById.set(milestone.id, { start, end })
      if (finished) finishedIds.add(milestone.id)
    } else if (start || end) partiallyDatedIds.add(milestone.id)
  })

  // Kahn's algorithm over every milestone: sources before targets, so every
  // node is only computed once all of its incoming edges' sources already
  // have an effective window (or have been resolved as unschedulable).
  const order: string[] = []
  const remainingIndegree = new Map(indegree)
  const queue: string[] = []
  milestones.forEach((m) => {
    if (!remainingIndegree.get(m.id)) queue.push(m.id)
  })
  while (queue.length) {
    const id = queue.shift() as string
    order.push(id)
    for (const nextId of outgoingBySource.get(id) || []) {
      const remaining = (remainingIndegree.get(nextId) || 0) - 1
      remainingIndegree.set(nextId, remaining)
      if (remaining === 0) queue.push(nextId)
    }
  }
  // Defensive only — the backend rejects circular dependencies at creation,
  // so this should never trigger, but a stray leftover node is still safer
  // computed on its own terms than silently dropped from the schedule.
  milestones.forEach((m) => {
    if (!order.includes(m.id)) order.push(m.id)
  })

  const effective = new Map<string, EffectiveScheduleEntry>()

  order.forEach((id) => {
    const milestone = milestoneById.get(id)
    // A milestone with only one of its two dates is the user's half-typed
    // plan, not a blank slate to generate over — left out, as it always was.
    if (!milestone || partiallyDatedIds.has(id)) return

    const own = ownWindowById.get(id) ?? null
    const edges = finishedIds.has(id) ? [] : incomingByTarget.get(id) || []

    // `duration_days` is inclusive; this function works in exclusive day-diffs.
    const durationDays = own
      ? own.end.diff(own.start, "day")
      : milestone.duration_days && milestone.duration_days > 0
        ? milestone.duration_days - 1
        : DEFAULT_MILESTONE_DURATION_DAYS - 1

    let startFloor: Dayjs | null = null
    let startFloorEdge: MilestoneDependency | null = null
    let endFloor: Dayjs | null = null
    let endFloorEdge: MilestoneDependency | null = null

    edges.forEach((dep) => {
      const sourceEffective = effective.get(dep.source_milestone)
      if (!sourceEffective) return

      const sourceSide = sourceSideFor(dep.type)
      // Once the source has actually finished, a Finish-based edge (FS, FF)
      // schedules off that real finish rather than the plan — the source's
      // own Planned End is untouched, only what its dependents anchor to
      // changes. Start-based edges (SS, SF) are unaffected: completion status
      // says nothing about when something started.
      const sourceActualEnd = actualEndById.get(dep.source_milestone)
      const sourceAnchor =
        sourceSide === "end" && sourceActualEnd
          ? sourceActualEnd
          : anchorOf(sourceEffective, sourceSide)

      // Not `lag_days || impliedGapDaysFor(...)`: that collapses lag 1 into
      // lag 0.
      const unbounded = sourceAnchor.add(
        impliedGapDaysFor(dep.type) + dep.lag_days,
        "day"
      )

      // An overlap may not reorder the two. Only start-anchored edges are
      // floored: Start → Finish ends its target as the source starts, so its
      // start legitimately precedes it.
      const sourceStart = anchorOf(sourceEffective, "start")
      const anchor =
        targetSideFor(dep.type) === "start" && unbounded.isBefore(sourceStart)
          ? sourceStart
          : unbounded

      if (targetSideFor(dep.type) === "start") {
        if (!startFloor || anchor.isAfter(startFloor)) {
          startFloor = anchor
          startFloorEdge = dep
        }
      } else {
        if (!endFloor || anchor.isAfter(endFloor)) {
          endFloor = anchor
          endFloorEdge = dep
        }
      }
    })

    // A stored start is never pulled earlier, only pushed later as far as the
    // strictest constraint demands; with no window of its own (undated, or
    // dated by the server from these same dependencies) the constraints alone
    // decide, earlier as well as later.
    let effectiveStart: Dayjs | null = own?.start ?? null
    let winningEdge: MilestoneDependency | null = null

    if (startFloor && (!effectiveStart || (startFloor as Dayjs).isAfter(effectiveStart))) {
      effectiveStart = startFloor
      winningEdge = startFloorEdge
    }
    if (endFloor) {
      const requiredStartFromEnd = (endFloor as Dayjs).subtract(durationDays, "day")
      if (!effectiveStart || requiredStartFromEnd.isAfter(effectiveStart)) {
        effectiveStart = requiredStartFromEnd
        winningEdge = endFloorEdge
      }
    }

    // Nothing stored and no dependency floor either — no entry, which both
    // views already read as "dates not set".
    if (!effectiveStart) return

    const effectiveEnd = effectiveStart.add(durationDays, "day")
    const isGenerated = !own
    const shiftedByDays = own ? effectiveStart.diff(own.start, "day") : 0

    effective.set(id, {
      start: effectiveStart,
      end: effectiveEnd,
      isShifted: shiftedByDays > 0,
      isGenerated,
      shiftedByDays,
      shiftReason:
        (isGenerated || shiftedByDays > 0) && winningEdge
          ? {
              sourceMilestoneId: (winningEdge as MilestoneDependency).source_milestone,
              type: (winningEdge as MilestoneDependency).type,
              lagDays: (winningEdge as MilestoneDependency).lag_days
            }
          : null,
      actualEnd: actualEndById.get(id) ?? null
    })
  })

  return effective
}

/**
 * Compares two effective-schedule results (typically "before" vs "after" a
 * pending edit, dependency creation, or dependency change) and reports every
 * milestone whose calculated dates actually differ — the same shape used to
 * both drive and word the "this will also move N milestones" confirmation.
 * `previousWindow` carries the "before" value so a confirmation can show the
 * actual before → after effect rather than just the destination.
 */
function diffEffectiveSchedule(
  before: Map<string, EffectiveScheduleEntry>,
  after: Map<string, EffectiveScheduleEntry>,
  options?: { excludeIds?: Set<string> }
): { id: string; window: DateWindow; previousWindow: DateWindow | null }[] {
  const changed: { id: string; window: DateWindow; previousWindow: DateWindow | null }[] = []
  after.forEach((entry, id) => {
    if (options?.excludeIds?.has(id)) return
    const previous = before.get(id)
    if (!previous || !previous.start.isSame(entry.start, "day") || !previous.end.isSame(entry.end, "day")) {
      changed.push({
        id,
        window: { start: entry.start, end: entry.end },
        previousWindow: previous ? { start: previous.start, end: previous.end } : null
      })
    }
  })
  return changed
}

export { calculateEffectiveSchedule, DEFAULT_MILESTONE_DURATION_DAYS, diffEffectiveSchedule }
export type { DateWindow, EffectiveScheduleEntry, EffectiveScheduleOverride, ShiftReason }
